import type { FilteredValue } from '@fitogest/reactive-form-builder/interfaces';
import { Uso } from 'src/app/models/productos/uso.model';
import type { TratamientosControlMagramaSlotData } from './form-definition';

/**
 * Converts MAGRAMA detail rows into repeatable dropdown data.
 */
export class TratamientosControlMagramaMapper {
    public static mapRows(rows: Uso[]): TratamientosControlMagramaSlotData {
        const usosFiltered: FilteredValue<string>[] = [];
        const agentesFiltered: FilteredValue<string>[] = [];
        const udsFiltered: Array<{ label: string; value: string | null }> = [];
        const dosisFiltered: Array<{ label: string; value: string | null }> = [];
        const agentesByUso: Record<string, FilteredValue<string>[]> = {};
        const udsByUso: Record<string, Array<{ label: string; value: string | null }>> = {};
        const dosisByUso: Record<string, Array<{ label: string; value: string | null }>> = {};
        const seenUsos = new Set<string>();
        const seenAgentes = new Set<string>();
        const seenAgentesByUso = new Map<string, Set<string>>();

        for (const row of rows) {
            TratamientosControlMagramaMapper.appendUso(row, usosFiltered, seenUsos);
            TratamientosControlMagramaMapper.appendAgente(row, agentesFiltered, udsFiltered, dosisFiltered, seenAgentes);
            TratamientosControlMagramaMapper.appendAgenteByUso(row, agentesByUso, udsByUso, dosisByUso, seenAgentesByUso);
        }

        return { usosFiltered, agentesFiltered, udsFiltered, dosisFiltered, agentesByUso, udsByUso, dosisByUso };
    }

    private static appendUso(
        row: Uso,
        usosFiltered: FilteredValue<string>[],
        seenUsos: Set<string>,
    ): void {
        if (!row.uso || seenUsos.has(row.uso)) {
            return;
        }

        seenUsos.add(row.uso);
        usosFiltered.push({ label: row.uso, value: row.uso });
    }

    private static appendAgente(
        row: Uso,
        agentesFiltered: FilteredValue<string>[],
        udsFiltered: Array<{ label: string; value: string | null }>,
        dosisFiltered: Array<{ label: string; value: string | null }>,
        seenAgentes: Set<string>,
    ): void {
        if (!row.agente || seenAgentes.has(row.agente)) {
            return;
        }

        seenAgentes.add(row.agente);
        TratamientosControlMagramaMapper.appendAgenteMetadata(row, agentesFiltered, udsFiltered, dosisFiltered);
    }

    private static appendAgenteByUso(
        row: Uso,
        agentesByUso: Record<string, FilteredValue<string>[]>,
        udsByUso: Record<string, Array<{ label: string; value: string | null }>>,
        dosisByUso: Record<string, Array<{ label: string; value: string | null }>>,
        seenAgentesByUso: Map<string, Set<string>>,
    ): void {
        if (!row.uso || !row.agente) {
            return;
        }

        const usoKey = TratamientosControlMagramaMapper.normalizeLookupText(row.uso);
        const agenteKey = TratamientosControlMagramaMapper.normalizeLookupText(row.agente);
        const seenForUso = seenAgentesByUso.get(usoKey) ?? new Set<string>();

        if (seenForUso.has(agenteKey)) {
            return;
        }

        seenForUso.add(agenteKey);
        seenAgentesByUso.set(usoKey, seenForUso);
        const agentesForUso = agentesByUso[usoKey] ?? [];
        const udsForUso = udsByUso[usoKey] ?? [];
        const dosisForUso = dosisByUso[usoKey] ?? [];

        agentesByUso[usoKey] = agentesForUso;
        udsByUso[usoKey] = udsForUso;
        dosisByUso[usoKey] = dosisForUso;
        TratamientosControlMagramaMapper.appendAgenteMetadata(row, agentesForUso, udsForUso, dosisForUso);
    }

    private static appendAgenteMetadata(
        row: Uso,
        agentesFiltered: FilteredValue<string>[],
        udsFiltered: Array<{ label: string; value: string | null }>,
        dosisFiltered: Array<{ label: string; value: string | null }>,
    ): void {
        const unidadMedida = row.unidad_medida || row.unidades_de_medida || row.unidad || '';

        agentesFiltered.push({ label: row.agente, value: row.agente });
        udsFiltered.push({ label: row.agente, value: unidadMedida || null });
        dosisFiltered.push({
            label: row.agente,
            value: `Dosis. ${row.dosis1}-${row.dosis2} ${unidadMedida} Plazo Seg. ${row.plazo_seguridad}`,
        });
    }

    private static normalizeLookupText(value: unknown): string {
        return String(value ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }
}
